import { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const NetflixRow = ({ title, children, id }) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const sliderRef = useRef(null);

  const scroll = (direction) => {
    const slider = sliderRef.current;
    if (slider) {
      const scrollAmount = 400;
      const newScrollLeft = direction === 'left' 
        ? slider.scrollLeft - scrollAmount 
        : slider.scrollLeft + scrollAmount;
      
      slider.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      
      // Update arrow visibility after scroll
      setTimeout(() => {
        checkArrows();
      }, 300);
    }
  };

  const checkArrows = () => {
    const slider = sliderRef.current;
    if (slider) {
      setShowLeftArrow(slider.scrollLeft > 0);
      setShowRightArrow(
        slider.scrollLeft < slider.scrollWidth - slider.clientWidth - 10
      );
    }
  };

  const handleScroll = () => {
    checkArrows();
  };

  return (
    <div className="netflix-row" id={id}>
      <h2 className="netflix-row-title">{title}</h2>
      <div className="netflix-row-container relative group">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/80 hover:bg-black text-white w-12 h-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}
        
        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/80 hover:bg-black text-white w-12 h-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        )}
        
        <div 
          className="netflix-slider"
          ref={sliderRef}
          onScroll={handleScroll}
          onLoad={checkArrows}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const NetflixCard = ({ title, subtitle, description, match, tags = [], onClick, imageSrc }) => {
  const getCardIcon = (title, subtitle) => {
    // Experience icons
    if (title.includes('Google')) return '🔍';
    if (title.includes('LinkedIn')) return '💼';
    if (title.includes('Discover')) return '🏦';
    
    // Expertise icons
    if (title.includes('Strategic Technology Risk')) return '🎯';
    if (title.includes('Cloud Security')) return '☁️';
    if (title.includes('AI Safety')) return '🤖';
    if (title.includes('Regulatory Compliance')) return '📋';
    if (title.includes('Continuous Controls')) return '🔄';
    if (title.includes('Product Security')) return '🛡️';
    
    // Project icons\n    if (title.includes('FedRAMP')) return '🏛️';
    if (title.includes('Gemini AI')) return '🧠';
    if (title.includes('Financial Controls')) return '💰';
    if (title.includes('Shared Fate')) return '🤝';
    if (title.includes('LinkedIn Common')) return '🔗';
    if (title.includes('Cyber Insurance')) return '🛡️';
    if (title.includes('Red Flags')) return '🚨';
    if (title.includes('NYDFS')) return '⚖️';
    
    // Publication icons
    if (title.includes('NIST')) return '📚';
    if (title.includes('CSA')) return '☁️';
    if (title.includes('Forbes')) return '📰';
    if (title.includes('Wharton') || title.includes('AI Risk')) return '🎓';
    
    // Education icons
    if (title.includes('MIT')) return '🎓';
    if (title.includes('University of Florida') || title.includes('Hough')) return '🐊';
    if (title.includes('Birla') || title.includes('BIT')) return '⚙️';
    
    // Certification icons
    if (title.includes('CCISO')) return '👔';
    if (title.includes('CISSP')) return '🔐';
    if (title.includes('CISM')) return '🛡️';
    if (title.includes('CRISC')) return '⚡';
    if (title.includes('CISA')) return '🔍';
    if (title.includes('PMP')) return '📊';
    if (title.includes('GCIH')) return '🚨';
    if (title.includes('GCSA')) return '☁️';
    
    // Award icons
    if (title.includes('NYDFS')) return '🏆';
    if (title.includes('FedRAMP Timeline')) return '🚀';
    if (title.includes('Industry Leadership')) return '🌟';
    if (title.includes('Cyber Insurance')) return '💎';
    
    // Default icon based on category
    return '📄';
  };

  const cardIcon = getCardIcon(title, subtitle);

  return (
    <div className="netflix-card" onClick={onClick}>
      <div className="netflix-card-image" style={imageSrc ? {backgroundImage: `url(${imageSrc})`, backgroundSize: 'cover', backgroundPosition: 'center'} : {}}>
        {!imageSrc && (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-netflix-red to-netflix-gray-200">
            <div className="text-4xl">{cardIcon}</div>
          </div>
        )}
      </div>
      <div className="netflix-card-content">
        <div className="netflix-card-title">{title}</div>
        <div className="netflix-card-subtitle">{subtitle}</div>
        {match && (
          <div className="netflix-match mb-2">{match}</div>
        )}
        {tags.length > 0 && (
          <div className="mb-2">
            {tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="netflix-badge netflix-badge-secondary">
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="netflix-card-description">{description}</div>
      </div>
    </div>
  );
};

export const NetflixLoadingCard = () => {
  return (
    <div className="netflix-card">
      <div className="netflix-loading" />
      <div className="netflix-card-content">
        <div className="h-4 bg-netflix-gray-200 rounded mb-2 animate-pulse" />
        <div className="h-3 bg-netflix-gray-200 rounded mb-2 animate-pulse" />
        <div className="h-3 bg-netflix-gray-200 rounded w-3/4 animate-pulse" />
      </div>
    </div>
  );
};